import { useEffect, useMemo, useState } from 'react';
import {
  DndContext,
  PointerSensor,
  pointerWithin,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CardDto, DayMode, Destination, EnvelopeResponse, WarningDto } from '@konvert/shared';
import { addDays, localDateOf } from '@konvert/domain';
import { ApiError, api } from './lib/api.js';
import { haptics, initTelegram, startParam } from './lib/telegram.js';
import { t } from './lib/i18n.js';
import { TodayScreen } from './screens/TodayScreen.js';
import { IndexScreen } from './screens/IndexScreen.js';
import { PiggybankScreen } from './screens/PiggybankScreen.js';
import { TriageScreen } from './screens/TriageScreen.js';
import type { NewCardPayload } from './components/NewCardForm.js';
import { AssistantSheet, type ChatMessage } from './components/AssistantSheet.js';
import { RulesScreen } from './screens/RulesScreen.js';
import { CardSheet } from './components/CardSheet.js';
import { ErrorNotice, LoadingNotice } from './components/Notice.js';
import { LimitConfirm, MoveDialog, type LimitPrompt } from './components/MoveDialog.js';

type Tab = 'day' | 'index' | 'piggybank' | 'triage' | 'rules';

const DRAWER_LABELS: Record<string, string> = {
  day: 'Конверт дня',
  week: 'Неделя',
  month: 'Месяц',
  year: 'Год',
  inbox: 'Бэклог',
  someday: 'Someday',
  projects: 'Проекты',
  done: 'Копилка',
  trash: 'Корзина',
  notes_archive: 'Архив заметок',
  carryover: 'Разбор',
};

export function App(): JSX.Element {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<Tab>('day');
  const [openCardId, setOpenCardId] = useState<string | null>(null);
  const [moveCard, setMoveCard] = useState<CardDto | null>(null);
  const [limitPrompt, setLimitPrompt] = useState<LimitPrompt | null>(null);
  /** The last thing worth knowing about the day, shown as a dismissible line. */
  const [warning, setWarning] = useState<string | null>(null);
  const [drawer, setDrawer] = useState<Destination | null>(null);
  const [assistantOpen, setAssistantOpen] = useState(false);
  const [chat, setChat] = useState<ChatMessage[]>([]);

  useEffect(() => {
    initTelegram();
    const param = startParam();
    if (param?.startsWith('card_')) setOpenCardId(param.slice(5));
    if (param?.startsWith('envelope_day_')) setTab('day');
  }, []);

  const settings = useQuery({ queryKey: ['settings'], queryFn: api.settings });
  const timezone = settings.data?.timezone ?? 'Europe/Belgrade';
  const threshold = settings.data?.settings.reschedule_threshold ?? 3;

  const day = useQuery({ queryKey: ['today'], queryFn: api.today });
  // The server decides what "today" is (SPEC 5.6); the device clock is only a
  // stand-in until the first answer arrives.
  const today = day.data?.date ?? localDateOf(new Date(), timezone);
  const strip = useQuery({
    queryKey: ['strip', today],
    queryFn: () => api.strip(today, addDays(today, 13)),
  });
  const piggybank = useQuery({
    queryKey: ['piggybank'],
    queryFn: api.piggybank,
    enabled: tab === 'piggybank',
  });
  const carryover = useQuery({
    queryKey: ['carryover'],
    queryFn: api.carryover,
    enabled: tab === 'triage',
  });
  const rules = useQuery({ queryKey: ['rules'], queryFn: api.rules, enabled: tab === 'rules' });
  const card = useQuery({
    queryKey: ['card', openCardId],
    queryFn: () => api.card(openCardId!),
    enabled: openCardId !== null,
  });
  const drawerQuery = useQuery<EnvelopeResponse>({
    queryKey: ['envelope', drawer],
    queryFn: () =>
      api.envelope(drawer!.kind, 'date' in drawer! ? (drawer as { date: string }).date : undefined),
    enabled: drawer !== null,
  });

  /**
   * The day limit no longer refuses anything (decision of 2026-09-29), so when
   * a card takes the day over it, the app says so and moves on.
   */
  const noteWarnings = (result: { warnings?: WarningDto[] } | undefined): void => {
    const limit = result?.warnings?.find((warning) => warning.code === 'LIMIT_EXCEEDED');
    if (!limit) {
      setWarning(null);
      return;
    }
    haptics.limit();
    setWarning(limit.message);
  };

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['today'] });
    void queryClient.invalidateQueries({ queryKey: ['strip'] });
    void queryClient.invalidateQueries({ queryKey: ['card'] });
    void queryClient.invalidateQueries({ queryKey: ['envelope'] });
    void queryClient.invalidateQueries({ queryKey: ['piggybank'] });
    void queryClient.invalidateQueries({ queryKey: ['carryover'] });
    void queryClient.invalidateQueries({ queryKey: ['rules'] });
  };

  /**
   * A rejected placement never sticks: the card stays where it was and the
   * limit dialog opens with the breakdown (SPEC 5.1, 11.3).
   */
  const handleFailure = (error: unknown, retry: (reason: string) => void): void => {
    if (error instanceof ApiError && error.payload.code === 'LIMIT_EXCEEDED') {
      haptics.limit();
      setLimitPrompt({
        message: error.payload.message,
        preview: error.payload.details?.preview,
        retry,
      });
      return;
    }
    haptics.error();
    if (error instanceof ApiError) window.alert(error.payload.message);
  };

  const move = useMutation({
    mutationFn: (vars: { card: CardDto; destination: Destination; force?: boolean; reason?: string }) =>
      api.move(vars.card.id, vars.destination, vars.force ?? false, vars.reason),
    onSuccess: (result) => {
      haptics.drop();
      setMoveCard(null);
      setLimitPrompt(null);
      noteWarnings(result);
      refresh();
    },
    onError: (error, vars) =>
      handleFailure(error, (reason) => move.mutate({ ...vars, force: true, reason })),
  });

  const create = useMutation({
    mutationFn: (payload: NewCardPayload & { force?: boolean; reason?: string }) =>
      api.createCard(payload),
    onSuccess: (result) => {
      haptics.drop();
      setLimitPrompt(null);
      noteWarnings(result);
      refresh();
    },
    onError: (error, vars) =>
      handleFailure(error, (reason) => create.mutate({ ...vars, force: true, reason })),
  });

  const complete = useMutation({
    mutationFn: (id: string) => api.complete(id),
    onSuccess: () => {
      haptics.done();
      setOpenCardId(null);
      refresh();
    },
  });

  const dropCard = useMutation({
    mutationFn: (id: string) => api.drop(id),
    onSuccess: () => {
      setOpenCardId(null);
      refresh();
    },
  });

  const update = useMutation({
    mutationFn: (vars: { id: string; patch: Record<string, unknown>; version: number }) =>
      api.patchCard(vars.id, vars.patch, vars.version),
    onSuccess: () => {
      haptics.drop();
      refresh();
    },
    onError: (error) => {
      haptics.error();
      if (error instanceof ApiError) window.alert(error.payload.message);
    },
  });

  const addNote = useMutation({
    mutationFn: (vars: { id: string; text: string }) => api.addNote(vars.id, vars.text),
    onSuccess: refresh,
  });

  const setWaiting = useMutation({
    mutationFn: (vars: { id: string; waitingFor: string; checkOn: string }) =>
      api.setWaiting(vars.id, vars.waitingFor, vars.checkOn),
    onSuccess: () => {
      setOpenCardId(null);
      refresh();
    },
  });

  const setMode = useMutation({
    mutationFn: (vars: { date: string; mode: DayMode }) => api.setDayMode(vars.date, vars.mode),
    onSuccess: refresh,
  });

  // Any failure of the screen's own data is shown, never swallowed.
  const failure =
    (tab === 'day' ? (day.error ?? strip.error) : null) ??
    (tab === 'index' ? (strip.error ?? drawerQuery.error) : null) ??
    (tab === 'piggybank' ? piggybank.error : null) ??
    (tab === 'triage' ? carryover.error : null) ??
    settings.error;
  const isLoading =
    (tab === 'day' && day.isLoading) ||
    (tab === 'index' && strip.isLoading) ||
    (tab === 'piggybank' && piggybank.isLoading) ||
    (tab === 'triage' && carryover.isLoading);

  const assistant = useMutation({
    mutationFn: (vars: { text: string; supersedes?: string }) =>
      api.assistantSend(vars.text, vars.supersedes),
    onMutate: (vars) => {
      setChat((current) => [
        ...current,
        { id: `u-${Date.now()}`, role: 'user', text: vars.text },
      ]);
    },
    onSuccess: (reply) => {
      setChat((current) => [
        ...current,
        {
          id: `a-${Date.now()}`,
          role: 'assistant',
          text: reply.text,
          proposalId: reply.proposalId,
          preview: reply.preview,
        },
      ]);
      refresh();
    },
    onError: (error) => {
      haptics.error();
      if (error instanceof ApiError) window.alert(error.payload.message);
    },
  });

  const applyProposal = useMutation({
    mutationFn: (vars: { id: string; force: boolean }) => api.applyProposal(vars.id, vars.force),
    onSuccess: () => {
      haptics.done();
      setChat((current) => [
        ...current,
        { id: `s-${Date.now()}`, role: 'assistant', text: 'Применил.' },
      ]);
      refresh();
    },
    onError: (error) => {
      haptics.error();
      if (error instanceof ApiError) window.alert(error.payload.message);
    },
  });

  const rejectProposal = useMutation({
    mutationFn: (id: string) => api.rejectProposal(id),
    onSuccess: () => {
      setChat((current) => [
        ...current,
        { id: `r-${Date.now()}`, role: 'assistant', text: 'Отклонил.' },
      ]);
      refresh();
    },
  });

  const addRule = useMutation({
    mutationFn: (text: string) => api.createRule({ kind: 'style', text }),
    onSuccess: refresh,
  });

  const toggleRule = useMutation({
    mutationFn: (rule: { id: string; status: string }) =>
      api.setRuleStatus(rule.id, rule.status === 'active' ? 'disabled' : 'active'),
    onSuccess: refresh,
  });

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  const onDragEnd = (event: DragEndEvent): void => {
    const destination = event.over?.data.current?.destination as Destination | undefined;
    const dragged = event.active.data.current?.card as CardDto | undefined;
    if (!destination || !dragged) return;
    move.mutate({ card: dragged, destination });
  };

  const drawerLabel = useMemo(
    () => (drawer ? (DRAWER_LABELS[drawer.kind] ?? drawer.kind) : ''),
    [drawer],
  );

  return (
    <DndContext
      sensors={sensors}
      // The card is wider than a date chip, so the pointer decides the target,
      // not the overlap of rectangles.
      collisionDetection={pointerWithin}
      onDragStart={() => haptics.pick()}
      onDragOver={() => haptics.over()}
      onDragEnd={onDragEnd}
    >
      <div className="mx-auto min-h-full w-full max-w-md px-3 pb-24 pt-3">
        {failure ? (
          <ErrorNotice
            error={failure}
            onRetry={() => {
              void queryClient.refetchQueries();
            }}
          />
        ) : null}

        {!failure && isLoading ? <LoadingNotice /> : null}

        {warning ? (
          <button
            type="button"
            onClick={() => setWarning(null)}
            className="mb-2 w-full rounded-[var(--radius-card)] border border-[var(--line)] bg-paper px-3 py-2 text-left text-xs text-ink"
          >
            {warning} <span className="text-ink-soft">· понятно</span>
          </button>
        ) : null}

        {tab === 'day' && day.data ? (
          <TodayScreen
            data={day.data}
            strip={strip.data ?? []}
            threshold={threshold}
            timezone={timezone}
            onOpenCard={(c) => setOpenCardId(c.id)}
            onCreate={(payload) => create.mutate(payload)}
            onSetMode={(mode) => setMode.mutate({ date: day.data!.date, mode })}
            onOpenTriage={() => setTab('triage')}
          />
        ) : null}

        {tab === 'index' ? (
          <IndexScreen
            strip={strip.data ?? []}
            today={today}
            opened={drawerQuery.data ?? null}
            openedLabel={drawerLabel}
            threshold={threshold}
            onPickDay={(date) => setDrawer({ kind: 'day', date })}
            onOpenDrawer={setDrawer}
            onOpenCard={(c) => setOpenCardId(c.id)}
          />
        ) : null}

        {tab === 'triage' && carryover.data ? (
          <TriageScreen
            cards={carryover.data.cards}
            today={today}
            threshold={threshold}
            onMove={(card, destination) => move.mutate({ card, destination })}
            onComplete={(card) => complete.mutate(card.id)}
            onDrop={(card) => dropCard.mutate(card.id)}
            onPickDate={(card) => setMoveCard(card)}
            onClose={() => setTab('day')}
          />
        ) : null}

        {tab === 'rules' && rules.data ? (
          <RulesScreen
            rules={rules.data.rules}
            onAdd={(text) => addRule.mutate(text)}
            onToggle={(rule) => toggleRule.mutate(rule)}
          />
        ) : null}

        {tab === 'piggybank' && piggybank.data ? <PiggybankScreen data={piggybank.data} /> : null}
      </div>

      <nav className="fixed inset-x-0 bottom-0 z-30 border-t border-[var(--line)] bg-paper pb-[env(safe-area-inset-bottom)]">
        <div className="mx-auto flex max-w-md">
          {(
            [
              ['day', t('nav.day')],
              ['index', t('nav.index')],
              ['piggybank', t('nav.piggybank')],
              ['rules', t('nav.rules')],
            ] as Array<[Tab, string]>
          ).map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setTab(key)}
              className={`flex-1 py-3 text-sm ${tab === key ? 'text-ink' : 'text-ink-soft'}`}
            >
              {label}
            </button>
          ))}
        </div>
      </nav>

      <button
        type="button"
        onClick={() => setAssistantOpen(true)}
        aria-label={t('assistant.title')}
        className="fixed bottom-20 right-4 z-30 h-12 w-12 rounded-full bg-[var(--ink)] text-lg text-[var(--paper)] shadow-lifted"
      >
        ✎
      </button>

      <AssistantSheet
        open={assistantOpen}
        messages={chat}
        pending={assistant.isPending}
        onClose={() => setAssistantOpen(false)}
        onSend={(text, supersedes) => assistant.mutate({ text, supersedes })}
        onApply={(id, force) => applyProposal.mutate({ id, force })}
        onReject={(id) => rejectProposal.mutate(id)}
      />

      <CardSheet
        card={card.data ?? null}
        open={openCardId !== null}
        timezone={timezone}
        onClose={() => setOpenCardId(null)}
        onComplete={() => openCardId && complete.mutate(openCardId)}
        onMove={() => {
          if (card.data) setMoveCard(card.data);
          setOpenCardId(null);
        }}
        onDrop={() => openCardId && dropCard.mutate(openCardId)}
        onAddNote={(text) => openCardId && addNote.mutate({ id: openCardId, text })}
        onWaiting={(waitingFor, checkOn) =>
          openCardId && setWaiting.mutate({ id: openCardId, waitingFor, checkOn })
        }
        onSave={(patch, version) =>
          openCardId && update.mutate({ id: openCardId, patch, version })
        }
      />

      <MoveDialog
        card={moveCard}
        strip={strip.data ?? []}
        today={today}
        onClose={() => setMoveCard(null)}
        onMove={(destination) => moveCard && move.mutate({ card: moveCard, destination })}
      />

      <LimitConfirm
        prompt={limitPrompt}
        onCancel={() => setLimitPrompt(null)}
        onConfirm={(reason) => limitPrompt?.retry(reason)}
      />
    </DndContext>
  );
}
